import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { compact, get, noop } from 'lodash-es';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getPagePublicationState } from 'site-modules/shared/utils/inventory/srp-utils';
import {
  getNoInventoryDealerlessMessageType,
  DEALERLESS_OEM_MSG_TYPE,
} from 'site-modules/shared/components/inventory/utils/no-inventory-message';
import {
  getSupportedWizardEngineTypes,
  isIncentivesWizardActive,
} from 'site-modules/inventory/utils/srp-top-message-utils';
import { getCpoMakeName } from 'site-modules/shared/utils/cpo-message';
import { PUB_STATES_LOWERCASE } from 'client/constants/pub-states';
import { Link } from 'site-modules/shared/components/link/link';
import { CertifiedPreOwnedMessage } from 'site-modules/shared/components/inventory/certified-pre-owned-message/certified-pre-owned-message';
import { TopMessageBox } from 'site-modules/shared/components/inventory/top-message-box/top-message-box';
import { CapOnePrequalApprovedMsg } from 'client/site-modules/shared/components/inventory/cap-one-prequal-approved-message/cap-one-prequal-approved-message';
import { SrpTopIncentives } from 'site-modules/shared/components/inventory/incentives-wizard/srp-top-incentives/srp-top-incentives';
import { IncentivesWizardWidget } from 'site-modules/shared/components/incentives/incentives-wizard/incentives-wizard-widget';
import { WithInitialWizardIncentives } from 'site-modules/inventory/components/srp-top-message/with-initial-wizard-incentives/with-initial-wizard-incentives';

import { SrpNarrativeText } from './srp-narrative-text/srp-narrative-text';

export const SrpTopMessage = memo(
  ({
    selectedFacets = null,
    inventoryData = null,
    pathName = null,
    pageName = null,
    isLocalDeliveryType = false,
    isExpandedRadiusDueToLowResults = false,
    hasCpoMessage = false,
    isMobile = false,
    onUpdate = noop,
    userCapOnePrequalApproved = false,
    onRemoveCapOnePrequalParam = noop,
    zipCode,
    paymentType,
    isCrawler = false,
    isNational = false,
  }) => {
    const attributes = get(inventoryData, 'attributes', {});
    const make = get(attributes, 'vehicle.make.name', '');
    const model = get(attributes, 'vehicle.model.name', '');
    const makeSlug = get(attributes, 'vehicle.make.niceId', '');
    const attrRadius = get(attributes, 'radius');
    const { pageNumber } = get(inventoryData, 'inventories', {});
    const selectedCarTypes = get(selectedFacets, 'inventoryType', []);

    const pagePublicationState = getPagePublicationState(selectedCarTypes);
    const isUsed = pagePublicationState === PUB_STATES_LOWERCASE.USED;

    const dealerlessMessageType = getNoInventoryDealerlessMessageType({
      makeSlug,
      selectedCarTypes,
    });

    const showExpandedRadiusMsg = isExpandedRadiusDueToLowResults && attrRadius === 6000 && pageNumber === 1;

    const narrativeApiData = get(inventoryData, 'seoInfo.narrativeWidget');
    const hasNarrativeApiData = !!narrativeApiData && narrativeApiData.items.length > 0;

    const engineTypes = getSupportedWizardEngineTypes(selectedFacets, inventoryData);

    const isWizardActive = isIncentivesWizardActive({
      inventoryData,
      pagePublicationState,
      engineTypes,
    });

    const latestYear = Math.max(
      ...get(inventoryData, 'inventories.results', []).map(result => get(result, 'vehicleInfo.styleInfo.year', 0))
    );
    const bodyType = get(inventoryData, 'inventories.results', []).find(
      result => get(result, 'vehicleInfo.styleInfo.bodyType', '') !== ''
    )?.vehicleInfo.styleInfo.bodyType;

    switch (true) {
      case userCapOnePrequalApproved: {
        return (
          <CapOnePrequalApprovedMsg
            isMobile={isMobile}
            onUpdate={onUpdate}
            onRemoveCapOnePrequalParam={onRemoveCapOnePrequalParam}
          />
        );
      }
      case !!dealerlessMessageType: {
        return (
          <TopMessageBox creativeId="search_banner_message">
            New {make} vehicles must be ordered directly from the manufacturer.{' '}
            {dealerlessMessageType === DEALERLESS_OEM_MSG_TYPE.NEW_ONLY && (
              <Link
                to={srpLinkBuilder({ ...selectedFacets, inventorytype: 'used,cpo' })}
                className="text-underline"
                data-tracking-id="inventory_view_filter_search_result"
                data-tracking-value="search-used-dealerless"
              >
                Click here to shop used {compact([make, model]).join(' ')} listings.
              </Link>
            )}
          </TopMessageBox>
        );
      }
      case showExpandedRadiusMsg: {
        return (
          <TopMessageBox creativeId="edm-entry-radius-expanion-message" iconClassName="icon-alarm">
            Search radius has been expanded in order to return matching results.
          </TopMessageBox>
        );
      }
      case hasNarrativeApiData: {
        return (
          <SrpNarrativeText
            narrativeApiData={narrativeApiData}
            pathname={pathName}
            pageName={pageName}
            className="mb-1_5 mb-md-1 text-cool-gray-30"
          />
        );
      }
      case hasCpoMessage: {
        return <CertifiedPreOwnedMessage make={getCpoMakeName(selectedFacets)} isMobile={isMobile} />;
      }
      case isUsed && isLocalDeliveryType: {
        return (
          <TopMessageBox iconClassName="icon-office" className="shop-5067-srp-top-local-results-message">
            Shopping local results available for in-store purchase
          </TopMessageBox>
        );
      }
      case isWizardActive: {
        return (
          <WithInitialWizardIncentives
            attributes={attributes}
            inventoryType={pagePublicationState}
            paymentType={paymentType}
          >
            {initialIncentives => (
              <IncentivesWizardWidget
                availableIncentives={initialIncentives}
                zipCode={zipCode}
                inventoryType={pagePublicationState}
                paymentType={paymentType}
                engineTypes={engineTypes}
              >
                {({ wizardState, incentives: wizardIncentives, onFinish, onStartOver }) => (
                  <SrpTopIncentives
                    incentives={wizardIncentives}
                    isMobile={isMobile}
                    attributes={get(inventoryData, 'attributes')}
                    engineTypes={engineTypes}
                    defaultYear={latestYear}
                    bodyType={bodyType}
                    pagePublicationState={pagePublicationState}
                    wizardState={wizardState}
                    onFinish={onFinish}
                    onStartOver={onStartOver}
                    isCrawler={isCrawler}
                    isNational={isNational}
                  />
                )}
              </IncentivesWizardWidget>
            )}
          </WithInitialWizardIncentives>
        );
      }
      default: {
        return null;
      }
    }
  }
);

SrpTopMessage.displayName = 'SrpTopMessage';

SrpTopMessage.propTypes = {
  selectedFacets: PropTypes.shape({}),
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({ pageNumber: PropTypes.number }),
    attributes: PropTypes.shape({
      vehicle: PropTypes.shape({
        make: PropTypes.shape({ name: PropTypes.string, niceId: PropTypes.string }),
        model: PropTypes.shape({ name: PropTypes.string }),
      }),
      radius: PropTypes.number,
      inventoryType: PropTypes.string,
    }),
    seoInfo: PropTypes.shape({
      narrativeWidget: PropTypes.shape({
        title: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            heading: PropTypes.string,
            content: PropTypes.arrayOf(PropTypes.string),
          })
        ),
        learnMoreText: PropTypes.string,
      }),
    }),
  }),
  pathName: PropTypes.string,
  pageName: PropTypes.string,
  isLocalDeliveryType: PropTypes.bool,
  isExpandedRadiusDueToLowResults: PropTypes.bool,
  hasCpoMessage: PropTypes.bool,
  isMobile: PropTypes.bool,
  userCapOnePrequalApproved: PropTypes.bool,
  onUpdate: PropTypes.func,
  onRemoveCapOnePrequalParam: PropTypes.func,
  zipCode: PropTypes.string.isRequired,
  paymentType: PropTypes.string.isRequired,
  isCrawler: PropTypes.bool,
  isNational: PropTypes.bool,
};
